import React, { useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';
import {
  DeleteOutline,
  Favorite,
} from '@material-ui/icons';

import PageLoading from '../../../../components/PageLoading';
import WishlistSkeleton from '../../../../components/PageLoading/skeletons/WishlistSkeleton';
import { useWishlist } from '../../../../context/WishlistContext';
import { fetchCosmeticLastSeen, formatLastSeen, SECTION_BRUSH, SECTION_ICONS } from '../../../../utils/cosmetics';

import './styles.scss';

const FILTERS = [
  { key: 'all', label: 'All Items' },
  { key: 'outfit', label: 'Outfits' },
  { key: 'glider', label: 'Gliders' },
  { key: 'wrap', label: 'Wraps' },
  { key: 'contrail', label: 'Contrails' },
  { key: 'shoe', label: 'Kicks' },
  { key: 'sidekick', label: 'Sidekicks' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest Added' },
  { value: 'oldest', label: 'Oldest Added' },
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
];

const CATEGORY_LABELS = {
  outfit: 'Outfit',
  backpack: 'Back Bling',
  pickaxe: 'Pickaxe',
  emote: 'Emote',
  emotes: 'Emote',
  glider: 'Glider',
  wrap: 'Wrap',
  contrail: 'Contrail',
  loadingscreen: 'Loading Screen',
  shoe: 'Kicks',
  sidekick: 'Sidekick',
};

function getItemSection(item) {
  const key = item.section || item.category || 'other';

  if (key === 'emotes') return 'emote';

  return key;
}

function formatAddedDate(addedAt) {
  if (!addedAt) return '';

  const date = new Date(addedAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return `Added ${date}`;
}

function WishlistCard({ item, onRemove, lastSeen, isLastSeenLoading }) {
  const section = getItemSection(item);
  const categoryLabel = CATEGORY_LABELS[section] || 'Item';

  return (
    <article className="wishlist-card">
      <div className={classnames('wishlist-card-inner', item.rarity, {
        emote: section === 'emote' || section === 'emotes',
      })}>
        <span className="wishlist-card-category">{categoryLabel}</span>
        <div className="wishlist-card-visual">
          <img
            className="wishlist-card-image"
            src={item.imageUrl}
            alt={item.name}
            draggable={false}
          />
        </div>

        <div className="wishlist-card-footer">
          <div className="wishlist-card-meta">
            <div className="wishlist-card-details">
              <h3 className="wishlist-card-name">{item.name}</h3>
              <div
                className="wishlist-card-last-seen"
                aria-busy={isLastSeenLoading}
                aria-live="polite"
              >
                {isLastSeenLoading ? (
                  <span
                    className="wishlist-card-last-seen-skeleton"
                    aria-hidden="true"
                  />
                ) : lastSeen ? (
                  <span className="wishlist-card-last-seen-text">
                    {formatLastSeen(lastSeen)}
                  </span>
                ) : (
                  <span className="wishlist-card-last-seen-empty" aria-hidden="true" />
                )}
              </div>
            </div>
            <p className="wishlist-card-date">{formatAddedDate(item.addedAt)}</p>
          </div>
          <button
            type="button"
            className="wishlist-card-remove"
            onClick={() => onRemove(item)}
          >
            REMOVE
            <DeleteOutline />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function WishlistView() {
  const { wishlist, removeFromWishlist, isLoading } = useWishlist();
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [lastSeenById, setLastSeenById] = useState({});
  const [lastSeenLoadingIds, setLastSeenLoadingIds] = useState(() => new Set());

  useEffect(() => {
    if (!wishlist.length) {
      setLastSeenById({});
      setLastSeenLoadingIds(new Set());
      return undefined;
    }

    let cancelled = false;

    const immediate = {};
    const toFetch = [];

    wishlist.forEach((item) => {
      if (item.lastSeen) {
        immediate[item.id] = item.lastSeen;
      } else {
        toFetch.push(item);
      }
    });

    setLastSeenById(immediate);
    setLastSeenLoadingIds(new Set(toFetch.map((item) => item.id)));

    if (!toFetch.length) return undefined;

    async function loadLastSeenDates() {
      const entries = await Promise.all(
        toFetch.map(async (item) => {
          try {
            const lastSeen = await fetchCosmeticLastSeen(item.id);
            return [item.id, lastSeen];
          } catch (error) {
            console.error(`Failed to fetch last seen for ${item.id}`, error);
            return [item.id, null];
          }
        })
      );

      if (cancelled) return;

      setLastSeenById((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
      setLastSeenLoadingIds(new Set());
    }

    loadLastSeenDates();

    return () => {
      cancelled = true;
    };
  }, [wishlist]);

  const stats = useMemo(() => {
    const outfits = wishlist.filter((item) => getItemSection(item) === 'outfit').length;
    const wraps = wishlist.filter((item) => getItemSection(item) === 'wrap').length;

    return {
      total: wishlist.length,
      outfits,
      wraps,
      other: wishlist.length - outfits - wraps,
    };
  }, [wishlist]);

  const filteredItems = useMemo(() => {
    let items = [...wishlist];

    if (activeFilter !== 'all') {
      items = items.filter((item) => getItemSection(item) === activeFilter);
    }

    items.sort((a, b) => {
      if (sortBy === 'newest') return (b.addedAt || 0) - (a.addedAt || 0);
      if (sortBy === 'oldest') return (a.addedAt || 0) - (b.addedAt || 0);
      if (sortBy === 'name-asc') return a.name.localeCompare(b.name);
      if (sortBy === 'name-desc') return b.name.localeCompare(a.name);
      return 0;
    });

    return items;
  }, [wishlist, activeFilter, sortBy]);

  return (
    <div className="wishlist-view page-loading-host page-loading-host--fill">
      <header className="wishlist-view-header">
        <div className="wishlist-view-title-wrap">
          <h1 className="wishlist-view-title">MY WISHLIST</h1>
          <img
            className="wishlist-view-brush"
            src={SECTION_BRUSH}
            alt=""
            aria-hidden
            draggable={false}
          />
        </div>
        <p className="wishlist-view-subtitle">
          Items you&apos;re waiting to return to the shop.
        </p>
      </header>

      <div className="page-content-area">
        {isLoading ? (
          <WishlistSkeleton />
        ) : (
          <>
            <div className="wishlist-view-stats">
              <div className="wishlist-view-stat">
                <Favorite className="wishlist-view-stat-icon" />
                <span className="wishlist-view-stat-value">{stats.total}</span>
                <span className="wishlist-view-stat-label">Total Items</span>
              </div>
              <div className="wishlist-view-stat">
                <img
                  className="wishlist-view-stat-icon wishlist-view-stat-icon--img"
                  src={SECTION_ICONS.outfit.src}
                  alt=""
                  aria-hidden
                  draggable={false}
                />
                <span className="wishlist-view-stat-value">{stats.outfits}</span>
                <span className="wishlist-view-stat-label">Outfits</span>
              </div>
              <div className="wishlist-view-stat">
                <img
                  className="wishlist-view-stat-icon wishlist-view-stat-icon--img"
                  src={SECTION_ICONS.wrap.src}
                  alt=""
                  aria-hidden
                  draggable={false}
                />
                <span className="wishlist-view-stat-value">{stats.wraps}</span>
                <span className="wishlist-view-stat-label">Wraps</span>
              </div>
              <div className="wishlist-view-stat">
                <img
                  className="wishlist-view-stat-icon wishlist-view-stat-icon--img"
                  src={SECTION_ICONS.glider.src}
                  alt=""
                  aria-hidden
                  draggable={false}
                />
                <span className="wishlist-view-stat-value">{stats.other}</span>
                <span className="wishlist-view-stat-label">Other</span>
              </div>
            </div>

            <div className="wishlist-view-toolbar">
              <div className="wishlist-view-filters">
                {FILTERS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={classnames('wishlist-view-filter', { active: activeFilter === key })}
                    onClick={() => setActiveFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <label className="wishlist-view-sort">
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  {SORT_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
            </div>

            {filteredItems.length > 0 ? (
              <div className="wishlist-view-grid">
                {filteredItems.map((item) => (
                  <WishlistCard
                    key={item.id}
                    item={item}
                    lastSeen={lastSeenById[item.id] ?? item.lastSeen}
                    isLastSeenLoading={lastSeenLoadingIds.has(item.id)}
                    onRemove={removeFromWishlist}
                  />
                ))}
              </div>
            ) : (
              <p className="wishlist-view-empty">
                {wishlist.length === 0
                  ? 'Your wishlist is empty. Head to the shop and add items you want to track.'
                  : 'No items match this filter.'}
              </p>
            )}

            <footer className="wishlist-view-footer">
              You&apos;ll be notified by email when items in your wishlist return to the shop.
            </footer>
          </>
        )}
      </div>

      {isLoading && (
        <PageLoading message="Loading your wishlist." />
      )}
    </div>
  );
}
